import React from 'react';
import PropTypes from 'prop-types';
import { Element } from 'react-scroll';
import { ListingType, ToggledFeatureKey } from '@domain-group/fe-allhomes-library';
import { AllhomesNonEuUser } from '@domain-group/fe-co-allhomes-eu-user';
import { useAllhomesApi } from '@domain-group/fe-co-allhomes-api-client';
import AllhomesEnquiryForm from '../../allhomes-enquiry-form';
import AllhomesEnquiryFormWithFieldChanges from '../../allhomes-enquiry-form-with-field-changes';
import AgencyDetails from './agency-details';
import agencyType from './props/agency-type';
import sponsorshipType from './props/sponsorship-type';
import * as styles from './listing-enquiry-form.style';
import { jsx as ___EmotionJSX } from "@emotion/core";
var DISCLAIMER_TEXT = 'Only to be used for sending genuine email enquiries to the Agent. All Homes Pty Ltd reserves its right to take any legal or other appropriate action in relation to misuse of this service.';
var TITLE_PREFIX = 'Please send me information on ';
var INSPECT = {
  id: 'INSPECT',
  value: 'INSPECT_THE_PROPERTY',
  label: 'Inspection time'
};
var PRICE = {
  id: 'PRICE',
  value: 'GET_AN_INDICATION_OF_PRICE',
  label: 'Price information'
};
var CONTRACT = {
  id: 'CONTRACT',
  value: 'OBTAIN_THE_CONTRACT_OF_SALE',
  label: 'Copy of the contract'
};
var SIMILAR = {
  id: 'SIMILAR',
  value: 'BE_CONTACTED_ABOUT_SIMILAR_PROPERTIES',
  label: 'Similar properties'
};
var SIMILAR_BUSINESS = {
  id: 'SIMILAR_BUSINESS',
  value: SIMILAR.value,
  label: 'Similar businesses'
};
var AVAILABILITY = {
  id: 'AVAILABILITY',
  value: 'KNOW_WHEN_THE_PROPERTY_IS_AVAILABLE',
  label: 'Availability'
};
var LEASE = {
  id: 'LEASE',
  value: 'FIND_OUT_THE_LENGTH_OF_THE_LEASE',
  label: 'Lease term'
};
var ONLINE_AUCTION = {
  id: 'ONLINE_AUCTION',
  value: 'REGISTER_FOR_ONLINE_AUCTION',
  label: 'Registering for an online auction'
};
var getOptions = (listingType, hasOnlineAuction) => {
  switch (listingType) {
    case ListingType.SALES_BUSINESS.name:
      return [PRICE, CONTRACT, SIMILAR_BUSINESS, INSPECT];
    case ListingType.RENTAL_RESIDENTIAL.name:
    case ListingType.RENTAL_COMMERCIAL.name:
    case ListingType.SHARE_RESIDENTIAL.name:
      return [INSPECT, AVAILABILITY, LEASE];
    case ListingType.SALES_RESIDENTIAL.name:
      return hasOnlineAuction ? [PRICE, CONTRACT, SIMILAR, INSPECT, ONLINE_AUCTION] : [PRICE, CONTRACT, SIMILAR, INSPECT];
    case ListingType.DEVELOPMENTS_RESIDENTIAL.name:
    case ListingType.SALES_COMMERCIAL.name:
    case ListingType.SALES_EARLY_ACCESS.name:
      return [PRICE, CONTRACT, SIMILAR, INSPECT];
    default:
      return null;
  }
};
var scrollPointForAgency = agency => "ListingEnquiryForm-Agency-".concat((agency === null || agency === void 0 ? void 0 : agency.agencyId) || (agency === null || agency === void 0 ? void 0 : agency.id) || 0);
var scrollPointForAgent = agent => scrollPointForAgency(agent === null || agent === void 0 ? void 0 : agent.agency);
var ListingEnquiryForm = _ref => {
  var _api$toggledFeatures;
  var listingType = _ref.listingType,
    hasOnlineAuction = _ref.hasOnlineAuction,
    appearance = _ref.appearance,
    endpointUrl = _ref.endpointUrl,
    listingAddress = _ref.listingAddress,
    recaptchaSiteKey = _ref.recaptchaSiteKey,
    onEnquirySent = _ref.onEnquirySent,
    onDoneClick = _ref.onDoneClick,
    sponsorship = _ref.sponsorship,
    defaultOrderedAgencies = _ref.agencies;
  var api = useAllhomesApi();

  // Render nothing if there are no agencies on this listing, or if the analytics token
  // doesn't exist (this will happen on first render since there's no token server-side)
  if (!defaultOrderedAgencies || !(api !== null && api !== void 0 && api.analyticsToken)) {
    return null;
  }

  // Generate a semi-random number out of the user's stable analytics token (UUID)
  var semiRandomNumber = ((api === null || api === void 0 ? void 0 : api.analyticsToken) || '0').split('').map(character => parseInt(character, 16)).filter(hexDigit => !Number.isNaN(hexDigit))
  // Roll up a 27-bit number out of the 128-bit UUID to make a simple message digest
  // eslint-disable-next-line no-bitwise
  .reduce((acc, next) => ((acc & 0x7ffffff) << 4 | acc >>> 27) ^ next, 0);

  // Shuffle the agencies into an new order that is specific to the user
  var randomAgencyIndex = semiRandomNumber % defaultOrderedAgencies.length;
  var agencies = [...defaultOrderedAgencies.slice(randomAgencyIndex), ...defaultOrderedAgencies.slice(0, randomAgencyIndex)];
  var options = getOptions(listingType, hasOnlineAuction);
  var formWithFieldChanges = (_api$toggledFeatures = api.toggledFeatures) === null || _api$toggledFeatures === void 0 ? void 0 : _api$toggledFeatures[ToggledFeatureKey.LISTING_ENQUIRY_FORM_FIELDS_CHANGES];
  var FormComponent = formWithFieldChanges ? AllhomesEnquiryFormWithFieldChanges : AllhomesEnquiryForm;
  return options ? ___EmotionJSX("div", null, agencies.map(agency => ___EmotionJSX(Element, {
    id: scrollPointForAgency(agency)
  })), ___EmotionJSX("div", {
    role: "form",
    css: styles.enquiryForm(appearance)
  }, appearance === 'listing' && ___EmotionJSX(AgencyDetails, {
    agencies: agencies
  }), ___EmotionJSX(AllhomesNonEuUser, null, ___EmotionJSX(FormComponent, {
    appearance: appearance,
    endpointUrl: endpointUrl,
    recaptchaSiteKey: recaptchaSiteKey,
    requirePhonenumber: true,
    enableSendMeCopy: true,
    formData: {},
    enableOther: true,
    options: options,
    formTitle: "".concat(TITLE_PREFIX).concat(listingAddress),
    disclaimer: DISCLAIMER_TEXT,
    onEnquirySent: onEnquirySent,
    onDoneClick: onDoneClick,
    listingAddress: listingAddress,
    sponsorship: sponsorship
  })))) : ___EmotionJSX("noscript", null);
};
ListingEnquiryForm.propTypes = {
  appearance: PropTypes.oneOf(['watchlist', 'listing']),
  endpointUrl: PropTypes.string.isRequired,
  recaptchaSiteKey: PropTypes.string.isRequired,
  listingType: PropTypes.oneOf(Object.keys(ListingType)).isRequired,
  listingAddress: PropTypes.string.isRequired,
  agencies: PropTypes.arrayOf(PropTypes.shape(agencyType)).isRequired,
  onEnquirySent: PropTypes.func,
  onDoneClick: PropTypes.func,
  hasOnlineAuction: PropTypes.bool,
  sponsorship: PropTypes.shape(sponsorshipType)
};
ListingEnquiryForm.defaultProps = {
  appearance: 'listing',
  onEnquirySent: () => {},
  onDoneClick: () => {},
  hasOnlineAuction: false,
  sponsorship: {}
};
export { scrollPointForAgency, scrollPointForAgent };
export default ListingEnquiryForm;